/**
 * Mídia da galeria do aparelho (ver `@mustauto/types/media-origin`): a data em
 * que a foto foi tirada, lida do EXIF, e o rótulo "Da galeria" que acompanha a
 * foto em toda tela que a mostra — app, painel e backoffice usam o mesmo texto.
 */
import type { MediaSource } from '@mustauto/types/media-origin';

/** Tags do EXIF com a data da foto, da mais à menos confiável. */
const EXIF_DATE_TAGS = ['DateTimeOriginal', 'DateTimeDigitized', 'DateTime'] as const;

/** Offset correspondente, quando o aparelho grava (`+HH:MM`). */
const EXIF_OFFSET_TAGS: Record<(typeof EXIF_DATE_TAGS)[number], string> = {
  DateTimeOriginal: 'OffsetTimeOriginal',
  DateTimeDigitized: 'OffsetTimeDigitized',
  DateTime: 'OffsetTime',
};

/** `2026:10:12 14:03:59` (o padrão EXIF) ou `2026-10-12 14:03:59`, com ou sem fração. */
const EXIF_DATE = /^(\d{4})[:-](\d{2})[:-](\d{2})[ T](\d{2}):(\d{2}):(\d{2})/;
const EXIF_OFFSET = /^([+-])(\d{2}):?(\d{2})$/;

/** Antes disso a data do EXIF é lixo (relógio zerado, `0000:00:00`). */
const MIN_PLAUSIBLE_YEAR = 2000;

/** Relógio do aparelho adiantado é comum; mais que um dia no futuro não é foto. */
const FUTURE_TOLERANCE_MS = 24 * 60 * 60 * 1000;

/**
 * Quando a foto da galeria foi tirada, em ms — ou `null` se o arquivo não diz
 * (print de tela, foto reenviada pelo WhatsApp, que apaga o EXIF) ou diz algo
 * impossível.
 *
 * O EXIF guarda a hora do relógio do aparelho que fotografou, quase sempre sem
 * fuso. Sem o `OffsetTime*`, a hora é lida no fuso de quem escolhe a foto — o
 * mesmo aparelho, na mesma oficina, na esmagadora maioria dos casos. Errar
 * algumas horas aqui não muda o que importa: a foto é de hoje ou de semanas
 * atrás.
 *
 * Aceita o objeto como o `expo-image-picker` devolve: no Android as tags no
 * nível de cima; no iOS o dicionário Exif no nível de cima e o TIFF mesclado.
 */
export function parseExifTakenAt(
  exif: Record<string, unknown> | null | undefined,
  now: number = Date.now(),
): number | null {
  if (!exif) return null;
  for (const tag of EXIF_DATE_TAGS) {
    const raw = exif[tag];
    if (typeof raw !== 'string') continue;
    const m = EXIF_DATE.exec(raw.trim());
    if (!m) continue;
    const [year, month, day, hour, minute, second] = m.slice(1).map(Number) as [
      number,
      number,
      number,
      number,
      number,
      number,
    ];
    if (year < MIN_PLAUSIBLE_YEAR || month < 1 || month > 12 || day < 1 || day > 31) continue;
    if (hour > 23 || minute > 59 || second > 59) continue;

    const offsetRaw = exif[EXIF_OFFSET_TAGS[tag]];
    const offset = typeof offsetRaw === 'string' ? EXIF_OFFSET.exec(offsetRaw.trim()) : null;
    let ms: number;
    if (offset) {
      const sign = offset[1] === '-' ? -1 : 1;
      const offsetMs = sign * (Number(offset[2]) * 60 + Number(offset[3])) * 60_000;
      ms = Date.UTC(year, month - 1, day, hour, minute, second) - offsetMs;
    } else {
      ms = new Date(year, month - 1, day, hour, minute, second).getTime();
    }
    // `new Date(2026, 1, 31)` vira 3 de março em silêncio: data que não existe
    // não é data da foto.
    const check = offset ? new Date(Date.UTC(year, month - 1, day)) : new Date(year, month - 1, day);
    const checkDay = offset ? check.getUTCDate() : check.getDate();
    if (checkDay !== day) continue;

    if (!Number.isFinite(ms) || ms > now + FUTURE_TOLERANCE_MS) continue;
    return ms;
  }
  return null;
}

/**
 * Folga entre a foto e a abertura da OS antes de o rótulo apontar a diferença.
 * A foto da galeria tirada minutos antes de abrir a OS (o mecânico fotografou
 * com a câmera do sistema enquanto o cliente explicava) é normal; um dia antes
 * já é outra conversa.
 */
export const GALLERY_BEFORE_OPENING_TOLERANCE_MS = 12 * 60 * 60 * 1000;

const DAY_MS = 24 * 60 * 60 * 1000;

function toMs(value: string | number | Date | null | undefined): number | null {
  if (value == null) return null;
  const ms = value instanceof Date ? value.getTime() : typeof value === 'number' ? value : Date.parse(value);
  return Number.isFinite(ms) ? ms : null;
}

/** Onde contar os dias e escrever a data. */
export interface MediaOriginOptions {
  /**
   * Fuso (IANA) — a API passa o da oficina (`WORKSHOP_TZ`), porque escreve a
   * página pública no servidor, que não tem leitor (Regra 13). Sem ele, o fuso
   * de quem roda: certo no app e no painel, que escrevem para quem lê.
   */
  timeZone?: string;
}

/** O dia do calendário (dias desde 1970) de um instante, no fuso pedido. */
function calendarDay(ms: number, timeZone?: string): number {
  if (!timeZone) {
    const d = new Date(ms);
    return Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()) / DAY_MS;
  }
  const { y, m, d } = datePartsIn(ms, timeZone);
  return Date.UTC(y, m - 1, d) / DAY_MS;
}

function datePartsIn(ms: number, timeZone: string): { y: number; m: number; d: number } {
  const parts = new Intl.DateTimeFormat('en-CA', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit' })
    .formatToParts(new Date(ms));
  const get = (type: string) => Number(parts.find((p) => p.type === type)?.value);
  return { y: get('year'), m: get('month'), d: get('day') };
}

/**
 * Quantos dias do CALENDÁRIO antes da abertura da OS a foto da galeria foi
 * tirada — `null` quando não dá para dizer (sem data, sem abertura), quando a
 * diferença cabe na folga (`GALLERY_BEFORE_OPENING_TOLERANCE_MS`) ou quando é
 * do mesmo dia. Com valor, a foto não prova como o carro chegou, e toda tela
 * a destaca.
 *
 * Dias do calendário, não blocos de 24h: a foto de sábado às 8h numa OS de
 * segunda às 7h é de "2 dias antes" — é o que a data dela diz, e era "1 dia"
 * contando horas.
 */
export function galleryDaysBeforeOpening(
  takenAt: string | number | Date | null | undefined,
  openedAt: string | number | Date | null | undefined,
  options: MediaOriginOptions = {},
): number | null {
  const taken = toMs(takenAt);
  const opened = toMs(openedAt);
  if (taken == null || opened == null) return null;
  if (opened - taken <= GALLERY_BEFORE_OPENING_TOLERANCE_MS) return null;
  const days = calendarDay(opened, options.timeZone) - calendarDay(taken, options.timeZone);
  return days >= 1 ? days : null;
}

function pad(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}

/** `DD/MM/AAAA` no fuso pedido (ou no de quem roda). */
function formatDateBR(ms: number, timeZone?: string): string {
  if (timeZone) {
    const { y, m, d } = datePartsIn(ms, timeZone);
    return `${pad(d)}/${pad(m)}/${y}`;
  }
  const d = new Date(ms);
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`;
}

/**
 * O rótulo da mídia que veio da galeria — `null` para a da câmera (e para a
 * mídia antiga, sem origem), que não leva rótulo nenhum.
 *
 * - sem data no arquivo: "Da galeria · sem data"
 * - tirada dias antes da abertura da OS: "Da galeria · tirada 3 dias antes da abertura"
 * - o resto: "Da galeria · tirada em 12/10/2026"
 *
 * `openedAt` é a criação da OS; sem ela, sempre a data. O fuso: ver
 * `MediaOriginOptions` — a API sempre passa o da oficina.
 */
export function mediaOriginLabel(
  source: MediaSource | string | null | undefined,
  takenAt: string | number | Date | null | undefined,
  openedAt?: string | number | Date | null,
  options: MediaOriginOptions = {},
): string | null {
  if (source !== 'gallery') return null;
  const taken = toMs(takenAt);
  if (taken == null) return 'Da galeria · sem data';
  const days = galleryDaysBeforeOpening(taken, openedAt, options);
  if (days != null) {
    return `Da galeria · tirada ${days} ${days === 1 ? 'dia' : 'dias'} antes da abertura`;
  }
  return `Da galeria · tirada em ${formatDateBR(taken, options.timeZone)}`;
}

/**
 * Versão curta, para selo sobre miniatura: "Galeria", ou "Galeria · 3 dias
 * antes" quando a foto é de dias antes da abertura. O texto inteiro
 * (`mediaOriginLabel`) vai no `title`/legenda.
 */
export function mediaOriginShortLabel(
  source: MediaSource | string | null | undefined,
  takenAt: string | number | Date | null | undefined,
  openedAt?: string | number | Date | null,
  options: MediaOriginOptions = {},
): string | null {
  if (source !== 'gallery') return null;
  const days = galleryDaysBeforeOpening(takenAt, openedAt, options);
  return days == null ? 'Galeria' : `Galeria · ${days} ${days === 1 ? 'dia' : 'dias'} antes`;
}
